import { useMemo } from 'react';

import {
  fetchLocationReferenceBundle,
  fetchReferenceBundle,
  type LocationReferenceBundle,
  type ReferenceBundle,
  type ReferenceBundleOptions,
} from '@/api/reference';
import { REFERENCE_STALE_TIME, useApiQuery } from '@/hooks/api';
import { queryKeys } from '@/shared/lib/queryKeys';

export function useReferenceData(
  options?: ReferenceBundleOptions & { enabled?: boolean },
) {
  const { enabled = true, ...bundleOptions } = options ?? {};

  return useApiQuery<ReferenceBundle>({
    queryKey: queryKeys.reference.bundle({
      candidateId: bundleOptions.candidateId,
      includeRoles: bundleOptions.includeRoles,
    }),
    queryFn: () => fetchReferenceBundle(bundleOptions),
    enabled,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useLocationReferenceData(options?: { enabled?: boolean }) {
  const { enabled = true } = options ?? {};

  return useApiQuery<LocationReferenceBundle>({
    queryKey: queryKeys.reference.location(),
    queryFn: fetchLocationReferenceBundle,
    enabled,
    staleTime: REFERENCE_STALE_TIME,
  });
}

const EMPTY_REFERENCE_BUNDLE: ReferenceBundle = {
  cities: [],
  skills: [],
  domains: [],
  seniority: [],
  certificates: [],
  education: [],
  roles: [],
  candidate: null,
};

export function useReferenceDataValues(
  options?: ReferenceBundleOptions & { enabled?: boolean },
) {
  const query = useReferenceData(options);

  const normalizedData = useMemo(() => {
    const data = query.data ?? EMPTY_REFERENCE_BUNDLE;

    return {
      ...data,
      cities: data.cities ?? [],
      skills: data.skills ?? [],
      domains: data.domains ?? [],
      seniority: data.seniority ?? [],
      certificates: data.certificates ?? [],
      education: data.education ?? [],
      roles: data.roles ?? [],
    };
  }, [query.data]);

  return { ...query, data: normalizedData };
}
